interface ProposalOutcomeLike {
  readonly outcomeIndex: number;
  readonly label?: string;
  readonly imageUrl?: string;
  readonly imageNsfw?: boolean;
  readonly funded?: string;
  readonly fundedPercent?: string;
}

interface ProposalRowLike {
  readonly outcomes?: ReadonlyArray<ProposalOutcomeLike>;
  readonly numOutcomes?: number;
  readonly outcomeLabels?: ReadonlyArray<string>;
}

function readField(raw: Record<string, unknown>, key: string): unknown {
  const nested = raw.payload;
  if (nested !== null && typeof nested === 'object' && key in nested) {
    return (nested as Record<string, unknown>)[key];
  }
  return raw[key];
}

function readString(raw: Record<string, unknown>, key: string): string {
  const value = readField(raw, key);
  return typeof value === 'string' ? value : '';
}

function readOutcomeIndex(raw: Record<string, unknown>): number | null {
  const value = readField(raw, 'outcomeIndex');
  const index = typeof value === 'bigint' ? Number(value) : value;
  return typeof index === 'number' && Number.isInteger(index) && index >= 0 && index < 10
    ? index
    : null;
}

export function appendOutcomeToProposal<T extends ProposalRowLike>(
  prev: T,
  raw: Record<string, unknown>,
): T {
  const label = readString(raw, 'finalLabel');
  const imageUrl = readString(raw, 'finalImageUrl');
  const outcomeIndex = readOutcomeIndex(raw);
  if (label.length === 0 || outcomeIndex === null) return prev;

  const existingOutcomes = prev.outcomes ?? [];
  if (existingOutcomes.some((outcome) => outcome.outcomeIndex === outcomeIndex)) return prev;

  const nextOutcomes = [
    ...existingOutcomes,
    {
      outcomeIndex,
      label,
      imageUrl,
      imageNsfw: false,
      funded: '0',
      fundedPercent: '0.00',
    },
  ].sort((left, right) => left.outcomeIndex - right.outcomeIndex);
  const nextNumOutcomes = Math.max(prev.numOutcomes ?? existingOutcomes.length, outcomeIndex + 1);
  const nextOutcomeLabels =
    prev.outcomeLabels === undefined
      ? undefined
      : Array.from({ length: nextNumOutcomes }, (_, index): string => {
          const outcome = nextOutcomes.find((candidate) => candidate.outcomeIndex === index);
          return outcome?.label ?? prev.outcomeLabels?.[index] ?? '';
        });

  return {
    ...prev,
    outcomes: nextOutcomes,
    numOutcomes: nextNumOutcomes,
    ...(nextOutcomeLabels === undefined ? undefined : { outcomeLabels: nextOutcomeLabels }),
  };
}
